import _ from "lodash";
import {
    FETCH_DOCUMENT_REPOSITORY_ATTACHMENTS_REQUEST,
    FETCH_DOCUMENT_REPOSITORY_ATTACHMENTS_FILTER_REQUEST,
    RECEIVE_DOCUMENT_REPOSITORY_ATTACHMENTS,
    RECEIVE_DOCUMENT_REPOSITORY_ATTACHMENTS_FILTER,
    EXPORT_DOCUMENT_REPOSITORY_ATTACHMENT_REQUEST, 
    EXPORT_DOCUMENT_REPOSITORY_ATTACHMENT_DONE, 
    MESSAGE_DOCUMENT_REPOSITORY_ATTACHMENT, 
    SET_ITEMS_OFFSET, SET_FILTERS,
    EXPORT_DOCUMENT_REPOSITORY_ATTACHMENTS_REQUEST,
    EXPORT_DOCUMENT_REPOSITORY_ATTACHMENTS_DONE,
    CLEAR_DOCUMENT_REPOSITORY_ATTACHMENTS_CACHE
} from '../../../constants/index'
import i18n from "@/languages/index";
import {
    downloadDocumentRepositoryAttachment,
    downloadDocumentRepositoryAttachments,
    fetchDocumentRepositoryAttachments as fetchAttachments,
    fetchDocumentRepositoryFilters
} from '../services/attachments';

const DOWNLOAD_WARNING_HEADER = 'x-ov-download-warnings';
const buildAttachmentsCacheKey = (filters = '', offset = 0, limit = 10) =>
    JSON.stringify({ filters, offset, limit });

const getResponseHeader = (headers, name) => {
    if (!headers) {
        return null;
    }

    if (typeof headers.get === 'function') {
        return headers.get(name);
    }

    return headers[name];
};

const parseDownloadWarnings = (response) => {
    const encodedWarnings = getResponseHeader(response?.headers, DOWNLOAD_WARNING_HEADER);
    if (!encodedWarnings) {
        return null;
    }

    try {
        return JSON.parse(atob(encodedWarnings));
    } catch (_error) {
        return null;
    }
};

const buildDownloadWarningMessage = (warnings) => {
    if (!warnings?.count) {
        return null;
    }

    const failedAttachmentNames = (warnings.attachments || [])
        .map((attachment) => attachment?.filename)
        .filter(Boolean);

    if (!failedAttachmentNames.length) {
        return i18n.t(
            'common:text.document_repository_download_warning_without_names',
            { count: warnings.count }
        );
    }

    return i18n.t(
        'common:text.document_repository_download_warning',
        {
            count: warnings.count,
            attachments: failedAttachmentNames.join(', ')
        }
    );
};

const buildDownloadFailureMessage = (attachments = []) => {
    const attachmentNames = attachments
        .map((attachment) => attachment?.filename || attachment?.name)
        .filter(Boolean);

    return attachmentNames.length
        ? buildDownloadWarningMessage({
            count: attachmentNames.length,
            attachments: attachmentNames.map((filename) => ({ filename }))
        })
        : null;
};

const downloadBlobResponse = (response, fallbackFilename = 'download.zip') => {
    const contentDisposition = getResponseHeader(response.headers, 'content-disposition');
    let filename = fallbackFilename;

    if (contentDisposition) {
        try {
            filename = contentDisposition.split(';').find(param => param.trim().startsWith('filename'))
                .split('=')[1].trim().replace(/^["']|["']$/g, '');
        } catch (_error) {
            filename = fallbackFilename;
        }
    }

    const blob = new Blob([response.data], {
        type: getResponseHeader(response.headers, 'content-type') || 'application/octet-stream'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.remove();
    }, 100);
};


export function fetchDocumentRepositoryAttachmentsRequest(initial) {
    const message = (initial)?null:i18n.t('common:text.document_repository_updating_list');

    return {
        type: FETCH_DOCUMENT_REPOSITORY_ATTACHMENTS_REQUEST,
        payload: {
            message,
        },
    };
}

export function fetchDocumentRepositoryAttachmentsFilterRequest() {
    return {
        type: FETCH_DOCUMENT_REPOSITORY_ATTACHMENTS_FILTER_REQUEST
    };
}

export function receiveDocumentRepositoryAttachments(
    response,
    initial,
    filters,
    errors = null,
    options = {}
) {
    const message = options.fromCache
        ? null
        : ((initial) ? null : i18n.t('common:text.document_repository_updated_list'));
    return {
        type: RECEIVE_DOCUMENT_REPOSITORY_ATTACHMENTS,
        payload: {
            response,
            message,
            filters,
            errors,
            cacheKey: options.cacheKey
        },
    };
}

export function receiveDocumentRepositoryAttachmentsFilter(payload) {
    return {
        type: RECEIVE_DOCUMENT_REPOSITORY_ATTACHMENTS_FILTER,
        payload
    };
}

export const messageDocumentRepositoryAttachment = message => {
    return {
        type: MESSAGE_DOCUMENT_REPOSITORY_ATTACHMENT,
        payload: {
            message
        }
    }
}

export function changeItemsOffset(newItemsOffset) {
    return {
        type: SET_ITEMS_OFFSET,
        payload: {
            itemsOffset: newItemsOffset
        }
    }
} 

export function changeFilter(newFilters, newItemsPage, newItemsOffset) {
    return {
        type: SET_FILTERS,
        payload: {
            itemsPage: newItemsPage,
            itemsOffset: newItemsOffset,
            currentFilter: newFilters
        }
    }
} 

export function clearPagination() {
    return async (dispatch) => {
        dispatch({
            type: CLEAR_INVOICE_PAGINATION
        });
    }
} 

export function clearAttachmentsCache() {
    return {
        type: CLEAR_DOCUMENT_REPOSITORY_ATTACHMENTS_CACHE
    };
}

export function fetchFilters(token, type) {
    return (dispatch) => {
        dispatch(fetchDocumentRepositoryAttachmentsFilterRequest());

        fetchDocumentRepositoryFilters(type)
        .then((response) => {
            dispatch(receiveDocumentRepositoryAttachmentsFilter(response));
        })
        .catch(error => {
            dispatch(receiveDocumentRepositoryAttachmentsFilter({}));
        });
    }
} 

export function fetchDocumentRepositoryAttachments(token, initial = false, filters = '', offset = 0, limit = 10, force = false) {
    return (dispatch, getState) => {
        const cacheKey = buildAttachmentsCacheKey(filters, offset, limit);
        const attachmentsCache = getState().documentRepository.attachmentsCache || {};
        const cachedResponse = attachmentsCache[cacheKey];

        if (!force && cachedResponse) {
            dispatch(
                receiveDocumentRepositoryAttachments(
                    cachedResponse,
                    initial,
                    filters,
                    null,
                    { cacheKey, fromCache: true }
                )
            );
            return;
        }

        if (force) {
            dispatch(clearAttachmentsCache());
        }

        dispatch(fetchDocumentRepositoryAttachmentsRequest(initial));
        fetchAttachments(filters, offset, limit)
        .then((response) => {
            dispatch(receiveDocumentRepositoryAttachments(response, initial, filters, null, { cacheKey }));
        })
        .catch((error) => {
            dispatch(receiveDocumentRepositoryAttachments([], initial, filters, error, { cacheKey }));
        });

    };
}

export function exportDocumentRepositoryAttachmentRequest(attachment) {
    const attachmentKey = attachment?.rowId || attachment?.id;
    return {
        type: EXPORT_DOCUMENT_REPOSITORY_ATTACHMENT_REQUEST,
        payload: {
            attachment,
            attachmentKey,
            message: null,
        },
    };
}

export function exportDocumentRepositoryAttachmentsRequest(filter) {
    return {
        type: EXPORT_DOCUMENT_REPOSITORY_ATTACHMENTS_REQUEST,
        payload: {
            filter,
            message: null,
        },
    };
}

export function exportDocumentRepositoryAttachmentDone(attachment) {
    const attachmentKey = attachment?.rowId || attachment?.id;
    return {
        type: EXPORT_DOCUMENT_REPOSITORY_ATTACHMENT_DONE,
        payload: {
            attachment,
            attachmentKey
        },
    };
}

export function exportDocumentRepositoryAttachmentsDone(filter) {
    return {
        type: EXPORT_DOCUMENT_REPOSITORY_ATTACHMENTS_DONE,
        payload: {
            filter
        },
    };
}

export function exportDocumentRepositoryAttachment(token, attachment) {

    return (dispatch) => {
        dispatch(exportDocumentRepositoryAttachmentRequest(attachment));
        dispatch(messageDocumentRepositoryAttachment(null));
        downloadDocumentRepositoryAttachment(attachment.id)
        .then(response => {
            if (!response) {
                console.error('Invalid response or filename provided.');
                return;
            }

            downloadBlobResponse(response, attachment?.filename || 'download');

            const warningMessage = buildDownloadWarningMessage(
                parseDownloadWarnings(response)
            );
            if (warningMessage) {
                dispatch(messageDocumentRepositoryAttachment(warningMessage));
            }
        })
        .catch(error => {
            const warningMessage = buildDownloadFailureMessage([attachment]);
            if (warningMessage) {
                dispatch(messageDocumentRepositoryAttachment(warningMessage));
            }
        })
        .finally(() => {
            dispatch(exportDocumentRepositoryAttachmentDone(attachment));
        });
    };
}

export function exportDocumentRepositoryAttachments(token, attachments = []) {
  return (dispatch) => {
    const uniqueAttachmentIds = [...new Set(
      attachments.map((attachment) => attachment.id).filter(Boolean)
    )];
    const attachmentIds = { attachment_ids: uniqueAttachmentIds };

    dispatch(exportDocumentRepositoryAttachmentsRequest(attachmentIds));
    dispatch(messageDocumentRepositoryAttachment(null));
    downloadDocumentRepositoryAttachments(uniqueAttachmentIds)
    .then(response => {
      downloadBlobResponse(response, 'download.zip');

      const warningMessage = buildDownloadWarningMessage(
        parseDownloadWarnings(response)
      );
      if (warningMessage) {
        dispatch(messageDocumentRepositoryAttachment(warningMessage));
      }
    })
    .catch(() => {
      const warningMessage = buildDownloadFailureMessage(attachments);
      if (warningMessage) {
        dispatch(messageDocumentRepositoryAttachment(warningMessage));
      }
    })
    .finally(() => {
      dispatch(exportDocumentRepositoryAttachmentsDone(attachmentIds));
    });
  };
}
